import React, { useEffect, useState } from 'react';
import { api, fmt, fmtDate, MONTHS } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, StatusBadge, EmptyState, Spinner, useToast, Modal } from '../components/UI';

export default function FranchiseePortal({ user, view, setView, directorView, onEnterDirectorView }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.getDeclarations(),
      api.getRoyalties(),
      api.getProfit(),
      api.getFranchises(),
    ]).then(([decls, roy, profit, franchises]) => {
      // Merge financials and salaries from franchises into profit data
      const franchiseMap = {};
      (franchises||[]).forEach(f => { franchiseMap[f.id] = f; });
      const mergedProfit = (profit||[]).map(p => ({
        ...p,
        financials: franchiseMap[p.franchiseId]?.financials || [],
        salaries: franchiseMap[p.franchiseId]?.salaries || [],
      }));
      setData({ declarations: decls||[], royalties: roy||[], profit: mergedProfit, franchises: franchises||[] });
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Dashboard" /><Spinner /></div>;

  const p = view || 'portal_dashboard';

  switch(p) {
    case 'portal_dashboard':    return <PortalDashboard user={user} data={data} setView={setView} isDirector={user?.role==='director'} onEnterDirectorView={onEnterDirectorView} />;
    case 'portal_declarations': return <PortalDeclarations user={user} data={data} onReload={load} />;
    case 'portal_royalties':    return <PortalRoyalties user={user} data={data} />;
    case 'portal_profit':       return <PortalProfit user={user} data={data} onReload={load} />;
    default:                    return <PortalDashboard user={user} data={data} setView={setView} isDirector={user?.role==='director'} onEnterDirectorView={onEnterDirectorView} />;
  }
}

// ─── Portal Dashboard ──────────────────────────────────────────────────────────
function PortalDashboard({ user, data, setView, isDirector, onEnterDirectorView }) {
  const { declarations, royalties, profit, franchises } = data;
  const userEmail = (user?.email||'').toLowerCase();
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function parseMonthDate(m) { if (!m) return 0; const p = String(m).split(' '); return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]); }
  function totalExp(m) { return (parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0); }
  function pct(v) { return (parseFloat(v)||0).toFixed(1)+'%'; }
  function getSal(f, email, month) {
    const sals = (f.salaries||[]).filter(s => (s.userEmail||'').toLowerCase()===email.toLowerCase() && parseMonthDate(s.effectiveFrom)<=parseMonthDate(month));
    sals.sort((a,b) => parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom));
    return sals.length ? parseFloat(sals[0].amount)||0 : 0;
  }
  function getShare(owners, email, month) {
    const entries = owners.filter(o => (o.email||'').toLowerCase()===email.toLowerCase());
    if (!entries.length) return 0;
    const applicable = entries.filter(o => {
      const from = parseMonthDate(o.effectiveFrom);
      const to = o.effectiveTo ? parseMonthDate(o.effectiveTo) : 999999;
      return from <= parseMonthDate(month) && parseMonthDate(month) < to;
    });
    const use = applicable.length ? applicable : entries.filter(o => parseMonthDate(o.effectiveFrom)<=parseMonthDate(month));
    use.sort((a,b)=>parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom));
    return use.length ? parseFloat(use[0].sharePercent)||0 : parseFloat(entries[0].sharePercent)||0;
  }

  const myFranchises = franchises.filter(f =>
    (f.owners||[]).filter(o=>o.isCurrent!==false).some(o => (o.email||'').toLowerCase()===userEmail)
  );
  const myFranchiseIds = new Set(myFranchises.map(f => f.id));
  const myDecls = declarations.filter(d =>
    d.franchiseeEmail?.toLowerCase()===userEmail ||
    d.franchiseeId===user?.id ||
    myFranchiseIds.has(d.franchiseId)
  );

  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [portalYear, setPortalYear] = useState(null);
  const [branchOpen, setBranchOpen] = useState({});

  const activeBranch = selectedBranchId ? myFranchises.find(f => f.id === selectedBranchId) : null;
  const displayFranchises = activeBranch ? [activeBranch] : myFranchises;

  // All years across selected branches
  const allYears = [...new Set(displayFranchises.reduce((acc,f) => {
    return acc.concat((f.financials||[]).map(m => { const match = (m.month||'').match(/\b(20\d\d)\b/); return match ? parseInt(match[1]) : null; }).filter(Boolean));
  }, []))].sort((a,b) => b-a);
  if (!allYears.length) allYears.push(new Date().getFullYear());
  const curYear = allYears.indexOf(portalYear) === -1 ? allYears[0] : portalYear;

  const yearFinancials = displayFranchises.reduce((acc,f) => {
    return acc.concat((f.financials||[]).filter(m => { const match = (m.month||'').match(/\b(20\d\d)\b/); return match && parseInt(match[1]) === curYear; }).map(m=>({...m,franchiseName:f.name})));
  }, []);
  const totalIncome = yearFinancials.reduce((s,m) => s+(parseFloat(m.income)||0), 0);
  const totalExpenses = yearFinancials.reduce((s,m) => s+totalExp(m), 0);
  const totalProfit2 = totalIncome - totalExpenses;
  const margin = totalIncome > 0 ? (totalProfit2/totalIncome*100).toFixed(1) : 0;
  const monthMap = {};
  yearFinancials.forEach(m => {
    if (!monthMap[m.month]) monthMap[m.month] = { month:m.month, income:0, rent:0, salaries:0, royalty:0, otherExpenses:0 };
    monthMap[m.month].income += parseFloat(m.income)||0;
    monthMap[m.month].rent += parseFloat(m.rent)||0;
    monthMap[m.month].salaries += parseFloat(m.salaries)||0;
    monthMap[m.month].royalty += parseFloat(m.royalty)||0;
    monthMap[m.month].otherExpenses += parseFloat(m.otherExpenses)||0;
  });
  const combinedMonths = Object.values(monthMap).sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month));

  // Overdue check
  const now = new Date();
  const prevMonth = MONTH_NAMES[now.getMonth()===0?11:now.getMonth()-1]+' '+(now.getMonth()===0?now.getFullYear()-1:now.getFullYear());

  // Find all overdue months across all franchises since royalty start
  const overdueMonths = [];
  myFranchises.forEach(f => {
    const startMonth = f.royaltyStartMonth || 'Sep 2025';
    const startIdx = parseMonthDate(startMonth);
    const dueIdx = now.getDate() > 10 ? parseMonthDate(prevMonth) : parseMonthDate(MONTH_NAMES[now.getMonth()===0?10:now.getMonth()-2]+' '+(now.getMonth()<=1?now.getFullYear()-1:now.getFullYear()));
    for (let idx = startIdx; idx <= dueIdx; idx++) {
      const yr = Math.floor(idx / 12);
      const mo = idx % 12;
      if (mo < 0 || mo > 11) continue;
      const monthStr = MONTH_NAMES[mo] + ' ' + yr;
      const hasDecl = myDecls.some(d => d.franchiseId === f.id && d.month === monthStr);
      if (!hasDecl) overdueMonths.push({ franchise: f.location||f.name, month: monthStr });
    }
  });
  const overdueWarning = overdueMonths.length > 0;

  // All-branch yearly summary
  const allYrMap = {};
  myFranchises.forEach(f => { (f.financials||[]).forEach(m => { const match=(m.month||'').match(/(20[0-9][0-9])/); if(!match) return; const yr=match[1]; if(!allYrMap[yr]) allYrMap[yr]={income:0,net:0}; allYrMap[yr].income+=parseFloat(m.income)||0; allYrMap[yr].net+=(parseFloat(m.income)||0)-totalExp(m); }); });
  const yearRows = Object.keys(allYrMap).sort((a,b)=>b-a).map(yr=>({year:yr,income:allYrMap[yr].income,net:allYrMap[yr].net,margin:allYrMap[yr].income>0?allYrMap[yr].net/allYrMap[yr].income*100:0}));

  const myProfit = profit.filter(f => (f.owners||[]).some(o=>(o.email||'').toLowerCase()===userEmail));
  const totalRemaining = myProfit.reduce((s,f)=>{ const me=(f.owners||[]).find(o=>(o.email||'').toLowerCase()===userEmail); return s+(me?((parseFloat(me.totalOwed)||0)-(parseFloat(me.totalTaken)||0)):0); },0);

  return (
    <div style={{ marginLeft:'var(--sidebar-width)' }}>
      <Topbar title={`Welcome, ${user?.name?.split(' ')[0]}`} subtitle={`${myFranchises.length} branch${myFranchises.length!==1?'es':''} assigned to your account`} actions={
        isDirector && onEnterDirectorView && (
          <button className="btn btn-secondary btn-sm" onClick={onEnterDirectorView}>
            <i className="ti ti-layout-dashboard" style={{ fontSize:13 }} /> Director View
          </button>
        )
      } />
      <div style={{ padding:24 }}>
        {overdueWarning && (
          <div style={{ background:'rgba(163,45,45,0.06)', border:'1px solid rgba(163,45,45,0.2)', borderRadius:'var(--r-lg)', padding:'14px 20px', marginBottom:24, display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(163,45,45,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>⚠</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:15, color:'var(--red)', marginBottom:3 }}>Outstanding Declarations</div>
              <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                {overdueMonths.slice(0,3).map((m,i) => (
                  <span key={i}><strong style={{ color:'var(--red)' }}>{m.franchise} — {m.month}</strong>{i < Math.min(overdueMonths.length,3)-1 ? ', ' : ''}</span>
                ))}
                {overdueMonths.length > 3 && <span> and {overdueMonths.length-3} more</span>}
              </div>
            </div>
            <button className="btn btn-sm" style={{ background:'var(--red)', color:'#fff', border:'none' }} onClick={() => setView('portal_declarations')}>Submit Now</button>
          </div>
        )}

        {myFranchises.length === 0 ? (
          <div className="card" style={{ padding:48, textAlign:'center', color:'var(--text-tertiary)' }}>No franchise assigned to your account yet.</div>
        ) : (
          <div>
            {/* KPI cards + year selector */}
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <span style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em' }}>
                  {activeBranch ? (activeBranch.location||activeBranch.name).toUpperCase() : 'ALL BRANCHES'}
                </span>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:allYears.indexOf(curYear)<allYears.length-1?1:0.3 }}
                    onClick={() => { const i=allYears.indexOf(curYear); if(i<allYears.length-1) setPortalYear(allYears[i+1]); }}>←</button>
                  <span style={{ fontSize:14, fontWeight:700, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{curYear}</span>
                  <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:allYears.indexOf(curYear)>0?1:0.3 }}
                    onClick={() => { const i=allYears.indexOf(curYear); if(i>0) setPortalYear(allYears[i-1]); }}>→</button>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                {[{l:'TOTAL REVENUE',v:fmt(totalIncome),c:'var(--blue)'},{l:'TOTAL EXPENSES',v:fmt(totalExpenses),c:'var(--red)'},{l:'NET PROFIT',v:fmt(totalProfit2),c:totalProfit2>=0?'var(--green)':'var(--red)'},{l:'MARGIN',v:`${margin}%`,c:'var(--text-secondary)'}].map(item => (
                  <div key={item.l} className="card" style={{ padding:'16px 18px', borderLeft:`3px solid ${item.c}` }}>
                    <div style={{ fontSize:10, color:'var(--text-tertiary)', marginBottom:6, letterSpacing:'0.08em' }}>{item.l}</div>
                    <div style={{ fontSize:22, fontWeight:700, color:item.c }}>{item.v}</div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:4 }}>{activeBranch?(activeBranch.location||activeBranch.name):'all branches'}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
              <div className="card" style={{ padding:'16px 18px', cursor:'pointer' }} onClick={() => setView('portal_declarations')}>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:6 }}>PENDING DECLARATIONS</div>
                <div style={{ fontSize:24, fontWeight:700, color:'var(--amber)' }}>{myDecls.filter(d=>d.status==='pending').length}</div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:4 }}>awaiting approval →</div>
              </div>
              <div className="card" style={{ padding:'16px 18px', cursor:'pointer' }} onClick={() => setView('portal_profit')}>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:6 }}>MY PROFIT BALANCE</div>
                <div style={{ fontSize:24, fontWeight:700, color:'var(--green)' }}>{fmt(totalRemaining)}</div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:4 }}>available balance →</div>
              </div>
              <div className="card" style={{ padding:'16px 18px', cursor:'pointer' }} onClick={() => setView('portal_royalties')}>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:6 }}>MY BRANCHES</div>
                <div style={{ fontSize:24, fontWeight:700, color:'var(--navy)' }}>{myFranchises.length}</div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:4 }}>view royalties →</div>
              </div>
            </div>

            {/* Branch selector */}
            {myFranchises.length > 1 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:8 }}>SELECT BRANCH</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-sm" style={{ background:!selectedBranchId?'var(--navy)':'#fff', color:!selectedBranchId?'#fff':'var(--text-secondary)', border:'1px solid var(--border)' }}
                    onClick={() => setSelectedBranchId(null)}>All Branches</button>
                  {myFranchises.map(f => (
                    <button key={f.id} className="btn btn-sm" style={{ background:selectedBranchId===f.id?'var(--navy)':'#fff', color:selectedBranchId===f.id?'#fff':'var(--text-secondary)', border:'1px solid var(--border)' }}
                      onClick={() => setSelectedBranchId(selectedBranchId===f.id?null:f.id)}>{f.location||f.name}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Combined monthly breakdown */}
            {combinedMonths.length > 0 && (
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>MONTHLY BREAKDOWN — {curYear}</div>
                <div className="card" style={{ overflow:'hidden', overflowX:'auto' }}>
                  <table className="table">
                    <thead><tr>
                      <th>MONTH</th>
              <th style={{ textAlign:'right' }}>INCOME</th><th style={{ textAlign:'right' }}>RENT</th><th style={{ textAlign:'right' }}>SALARIES</th><th style={{ textAlign:'right' }}>ROYALTY</th><th style={{ textAlign:'right' }}>OTHER</th><th style={{ textAlign:'right' }}>TOTAL EXP</th><th style={{ textAlign:'right' }}>NET</th>
                    </tr></thead>
                    <tbody>
                      {combinedMonths.map(m => { const exp=totalExp(m); const net=(parseFloat(m.income)||0)-exp; return (
                        <tr key={m.month}>
                          <td style={{ color:'var(--text-secondary)' }}>{m.month}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(m.income)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.rent||0)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.salaries||0)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.royalty||0)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.otherExpenses||0)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--red)', fontWeight:500 }}>{fmt(exp)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:700, color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</td>
                        </tr>
                      ); })}
                      <tr style={{ borderTop:'2px solid var(--border)', background:'rgba(28,25,96,0.06)', fontWeight:700 }}>
                        <td style={{ fontSize:11, color:'var(--text-tertiary)' }}>TOTAL</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(totalIncome)}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(combinedMonths.reduce((s,m)=>s+(parseFloat(m.rent)||0),0))}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(combinedMonths.reduce((s,m)=>s+(parseFloat(m.salaries)||0),0))}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(combinedMonths.reduce((s,m)=>s+(parseFloat(m.royalty)||0),0))}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(combinedMonths.reduce((s,m)=>s+(parseFloat(m.otherExpenses)||0),0))}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--red)' }}>{fmt(totalExpenses)}</td>
                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:totalProfit2>=0?'var(--green)':'var(--red)' }}>{fmt(totalProfit2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* All branches: Monthly Margin History + Yearly Margins + Franchisee P&L */}
            {!activeBranch && myFranchises.length > 1 && combinedMonths.length > 0 && (() => {
              const yInc=combinedMonths.reduce((s,m)=>s+(parseFloat(m.income)||0),0);
              const yNet=yInc-combinedMonths.reduce((s,m)=>s+totalExp(m),0);
              return (
                <div>
                  {/* Monthly Margin History */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>MONTHLY MARGIN HISTORY — ALL BRANCHES</div>
                    <div className="card" style={{ overflow:'hidden' }}>
                      <table className="table">
                        <thead><tr><th>MONTH</th><th>INCOME</th><th>NET PROFIT</th><th>MARGIN</th></tr></thead>
                        <tbody>
                          {combinedMonths.map(m => { const net=(parseFloat(m.income)||0)-totalExp(m); const mg=parseFloat(m.income)>0?(net/parseFloat(m.income)*100).toFixed(1):'0.0'; return (
                            <tr key={m.month}>
                              <td style={{ color:'var(--text-secondary)' }}>{m.month}</td>
                              <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(m.income)}</td>
                              <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</td>
                              <td><span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:parseFloat(mg)>=0?'rgba(15,110,86,0.1)':'rgba(163,45,45,0.1)', color:parseFloat(mg)>=0?'var(--green)':'var(--red)' }}>{mg}%</span></td>
                            </tr>
                          ); })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Yearly Margins */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>YEARLY MARGINS — ALL BRANCHES</div>
                    <div className="card" style={{ overflow:'hidden' }}>
                      <table className="table">
                        <thead><tr><th>YEAR</th><th>REVENUE</th><th>NET PROFIT</th><th>MARGIN</th></tr></thead>
                        <tbody>
                          {yearRows.map(yr => (
                            <tr key={yr.year}>
                              <td style={{ fontWeight:600 }}>{yr.year}</td>
                              <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(yr.income)}</td>
                              <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:yr.net>=0?'var(--green)':'var(--red)' }}>{fmt(yr.net)}</td>
                              <td><span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:yr.margin>=0?'rgba(15,110,86,0.1)':'rgba(163,45,45,0.1)', color:yr.margin>=0?'var(--green)':'var(--red)' }}>{pct(yr.margin)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Franchisee P&L — all branches combined */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>FRANCHISEE PROFIT & LOSS — ALL BRANCHES</div>
                    <div className="card" style={{ overflow:'hidden', overflowX:'auto' }}>
                      <table className="table">
                        <thead><tr>
                          <th>MONTH</th>
              <th>FRANCHISEE</th><th style={{ textAlign:'right' }}>NET PROFIT</th>
                          <th style={{ textAlign:'right', color:'var(--green)' }}>SHARE</th>
                          <th style={{ textAlign:'right', color:'var(--blue)' }}>SALARY</th>
                          <th style={{ textAlign:'right' }}>TH</th>
                        </tr></thead>
                        <tbody>
                          {(() => {
                            const ownerMap = {};
                            myFranchises.forEach(f => { (f.owners||[]).forEach(o => { const k=(o.email||'').toLowerCase(); if(!ownerMap[k]) ownerMap[k]={name:o.name,email:o.email}; }); });
                            const allOwners = Object.values(ownerMap);
                            const monthSet = {};
                            myFranchises.forEach(f => { (f.financials||[]).forEach(m => { const match=(m.month||'').match(/\b(20\d\d)\b/); if(match&&parseInt(match[1])===curYear) monthSet[m.month]=true; }); });
                            const allMonths2 = Object.keys(monthSet).sort((a,b)=>parseMonthDate(b)-parseMonthDate(a));
                            const rows = [];
                            allMonths2.forEach(month => {
                              const monthNet = myFranchises.reduce((s,f) => { const fm=(f.financials||[]).find(x=>x.month===month); return fm?s+(parseFloat(fm.income)||0)-totalExp(fm):s; },0);
                              const ownerFigs = allOwners.map(o => {
                                let share=0, salary=0;
                                myFranchises.forEach(f => {
                                  const fo=(f.owners||[]).find(x=>(x.email||'').toLowerCase()===(o.email||'').toLowerCase());
                                  if(!fo) return;
                                  const fm=(f.financials||[]).find(x=>x.month===month);
                                  if(!fm) return;
                                  const fNet=(parseFloat(fm.income)||0)-totalExp(fm);
                                  const fSal=getSal(f,o.email,month);
                                  const allSals=(f.owners||[]).reduce((s,oo)=>s+getSal(f,oo.email,month),0);
                                  salary+=fSal;
                                  share+=(fNet-allSals)*parseFloat(fo.sharePercent||0)/100;
                                });
                                return {owner:o,share,salary,th:share+salary};
                              });
                              ownerFigs.forEach((fig,oi) => {
                                const isMe=(fig.owner.email||'').toLowerCase()===userEmail;
                                rows.push(
                                  <tr key={month+'_'+oi} style={{ borderBottom:oi===ownerFigs.length-1?'2px solid var(--border)':'1px solid rgba(28,25,96,0.04)', background:isMe?'rgba(15,110,86,0.02)':undefined }}>
                                    {oi===0 && <td rowSpan={ownerFigs.length} style={{ color:'var(--text-secondary)', verticalAlign:'top' }}>{month}</td>}
                                    <td style={{ color:isMe?'var(--navy)':'var(--text-secondary)', fontWeight:isMe?600:400 }}>
                                      {(fig.owner.name||'').split(' ')[0]}
                                      {isMe && <span style={{ marginLeft:6, fontSize:9, color:'var(--green)', background:'rgba(15,110,86,0.1)', padding:'2px 6px', borderRadius:10 }}>you</span>}
                                    </td>
                                    {oi===0 && <td rowSpan={ownerFigs.length} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:monthNet>=0?'var(--blue)':'var(--red)', verticalAlign:'top' }}>{fmt(monthNet)}</td>}
                                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:fig.share>=0?'var(--green)':'var(--red)' }}>{fmt(fig.share)}</td>
                                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fig.salary>0?fmt(fig.salary):'—'}</td>
                                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:isMe?700:400, color:isMe?'var(--green)':'var(--text-secondary)' }}>{fmt(fig.th)}</td>
                                  </tr>
                                );
                              });
                            });
                            // Yearly totals
                            allOwners.forEach((o,oi) => {
                              const isMe=(o.email||'').toLowerCase()===userEmail;
                              let tShare=0,tSal=0;
                              myFranchises.forEach(f => {
                                const fo=(f.owners||[]).find(x=>(x.email||'').toLowerCase()===(o.email||'').toLowerCase());
                                if(!fo) return;
                                (f.financials||[]).filter(m=>{const match=(m.month||'').match(/\b(20\d\d)\b/);return match&&parseInt(match[1])===curYear;}).forEach(fm=>{
                                  const fNet=(parseFloat(fm.income)||0)-totalExp(fm);
                                  const fSal=getSal(f,o.email,fm.month);
                                  const allSals=(f.owners||[]).reduce((s,oo)=>s+getSal(f,oo.email,fm.month),0);
                                  tSal+=fSal; tShare+=(fNet-allSals)*parseFloat(fo.sharePercent||0)/100;
                                });
                              });
                              rows.push(
                                <tr key={'ytot'+oi} style={{ borderTop:oi===0?'2px solid var(--border)':'none', background:'rgba(28,25,96,0.06)', fontWeight:700 }}>
                                  {oi===0 && <td rowSpan={allOwners.length} style={{ fontSize:11, color:'var(--text-tertiary)', verticalAlign:'top' }}>YEAR TOTAL</td>}
                                  <td style={{ color:isMe?'var(--navy)':'var(--text-secondary)', fontSize:11 }}>{(o.name||'').split(' ')[0]}</td>
                                  {oi===0 && <td rowSpan={allOwners.length} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, verticalAlign:'top' }}>{fmt(yNet)}</td>}
                                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(tShare)}</td>
                                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{tSal>0?fmt(tSal):'—'}</td>
                                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:isMe?'var(--green)':'var(--text-secondary)' }}>{fmt(tShare+tSal)}</td>
                                </tr>
                              );
                            });
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Individual branch details */}
            {!activeBranch && myFranchises.length > 1 && (
              <div style={{ borderTop:'2px solid var(--border)', paddingTop:24, marginTop:8, marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                  <div style={{ flex:1, height:1, background:'var(--border)' }} />
                  <span style={{ fontSize:11, color:'var(--text-tertiary)', letterSpacing:'0.1em', whiteSpace:'nowrap' }}>INDIVIDUAL BRANCH DETAILS</span>
                  <div style={{ flex:1, height:1, background:'var(--border)' }} />
                </div>
              </div>
            )}

            {displayFranchises.map(f => {
              const isOpen = !activeBranch ? (branchOpen[f.id] !== false) : true;
              const sorted = [...(f.financials||[])].sort((a,b)=>parseMonthDate(b.month)-parseMonthDate(a.month));
              const latest = sorted[0];
              const prev = sorted[1]||null;
              if (!latest) return null;
              const latestNet = (parseFloat(latest.income)||0)-totalExp(latest);
              const latestMargin = parseFloat(latest.income)>0?latestNet/parseFloat(latest.income)*100:0;
              const prevMarginV = prev&&parseFloat(prev.income)>0?((parseFloat(prev.income)||0)-totalExp(prev))/parseFloat(prev.income)*100:null;
              const marginDiff = prevMarginV!==null?latestMargin-prevMarginV:null;
              // Year maps for this branch
              const brYrMap = {};
              sorted.forEach(m => { const match=(m.month||'').match(/(20[0-9][0-9])/); if(!match) return; const yr=match[1]; if(!brYrMap[yr]) brYrMap[yr]={income:0,net:0}; brYrMap[yr].income+=parseFloat(m.income)||0; brYrMap[yr].net+=(parseFloat(m.income)||0)-totalExp(m); });
              const brYearRows = Object.keys(brYrMap).sort((a,b)=>b-a).map(yr=>({year:yr,income:brYrMap[yr].income,net:brYrMap[yr].net,margin:brYrMap[yr].income>0?brYrMap[yr].net/brYrMap[yr].income*100:0}));
              // Financials year
              const brAllYrs = brYearRows.map(y=>parseInt(y.year));
              const brCurYr = portalYear&&brAllYrs.includes(parseInt(portalYear))?parseInt(portalYear):brAllYrs[0]||new Date().getFullYear();
              const brYrIdx = brAllYrs.indexOf(brCurYr);
              const brYrMths = sorted.filter(m=>{const match=(m.month||'').match(/(20[0-9][0-9])/);return match&&parseInt(match[1])===brCurYr;});
              const brYInc=brYrMths.reduce((s,m)=>s+(parseFloat(m.income)||0),0);
              const brYExp=brYrMths.reduce((s,m)=>s+totalExp(m),0);
              // P&L
              const owners = (f.owners||[]).filter(o=>o.name&&o.sharePercent&&o.isCurrent!==false);
              const plAllYrs = brYearRows.map(y=>y.year);
              const invYear = portalYear&&plAllYrs.includes(String(portalYear))?String(portalYear):(plAllYrs[0]||String(new Date().getFullYear()));
              const plMths = sorted.filter(m=>{const match=(m.month||'').match(/(20[0-9][0-9])/);return match&&match[1]===invYear;});

              return (
                <div key={f.id} style={{ marginBottom:16 }}>
                  {!activeBranch && myFranchises.length > 1 && (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:isOpen?'var(--r-lg) var(--r-lg) 0 0':'var(--r-lg)', cursor:'pointer' }}
                      onClick={() => setBranchOpen(p=>({...p,[f.id]:!isOpen}))}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{f.location||f.name}</div>
                        <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:2 }}>{f.location?f.name:''}</div>
                      </div>
                      <span style={{ color:'var(--text-tertiary)', fontSize:14 }}>{isOpen?'▲':'▼'}</span>
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ border:!activeBranch&&myFranchises.length>1?'1px solid var(--border)':'none', borderTop:'none', borderRadius:'0 0 var(--r-lg) var(--r-lg)', padding:!activeBranch&&myFranchises.length>1?16:0 }}>
                      {/* Margin summary cards */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:20 }}>
                        <div className="card" style={{ padding:16, borderLeft:'3px solid var(--blue)' }}>
                          <div style={{ fontSize:10, color:'var(--text-tertiary)', marginBottom:6, letterSpacing:'0.08em' }}>LATEST MARGIN</div>
                          <div style={{ fontSize:22, fontWeight:700, color:'var(--blue)' }}>{pct(latestMargin)}</div>
                          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:3 }}>{latest.month}</div>
                          {marginDiff!==null && <div style={{ fontSize:11, marginTop:4, color:marginDiff>=0?'var(--green)':'var(--red)' }}>{marginDiff>=0?'▲':'▼'} {Math.abs(marginDiff).toFixed(1)}% vs prev</div>}
                        </div>
                        <div className="card" style={{ padding:16, borderLeft:'3px solid var(--green)' }}>
                          <div style={{ fontSize:10, color:'var(--text-tertiary)', marginBottom:6, letterSpacing:'0.08em' }}>PREVIOUS MONTH</div>
                          <div style={{ fontSize:22, fontWeight:700, color:'var(--green)' }}>{prevMarginV!==null?pct(prevMarginV):'—'}</div>
                          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:3 }}>{prev?prev.month:'No data'}</div>
                        </div>
                        <div className="card" style={{ padding:16, borderLeft:'3px solid var(--red)' }}>
                          <div style={{ fontSize:10, color:'var(--text-tertiary)', marginBottom:6, letterSpacing:'0.08em' }}>THIS YEAR ({brYearRows[0]?.year||'—'})</div>
                          <div style={{ fontSize:22, fontWeight:700, color:'var(--red)' }}>{brYearRows[0]?pct(brYearRows[0].margin):'—'}</div>
                          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:3 }}>{brYearRows[0]?fmt(brYearRows[0].net)+' net profit':''}</div>
                        </div>
                      </div>

                      {/* Yearly margins */}
                      {brYearRows.length > 0 && (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>YEARLY MARGINS</div>
                          <div className="card" style={{ overflow:'hidden' }}>
                            <table className="table">
                              <thead><tr><th>YEAR</th><th>TOTAL INCOME</th><th>TOTAL EXPENSES</th><th>NET PROFIT</th><th>MARGIN</th></tr></thead>
                              <tbody>
                                {brYearRows.map(y => {
                                  const exp2=y.income-y.net;
                                  return (
                                    <tr key={y.year}>
                                      <td style={{ fontWeight:700, fontSize:14 }}>{y.year}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--blue)', fontWeight:600 }}>{fmt(y.income)}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--red)', fontWeight:600 }}>{fmt(exp2)}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', color:y.net>=0?'var(--green)':'var(--red)', fontWeight:700 }}>{fmt(y.net)}</td>
                                      <td>
                                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                          <div style={{ flex:1, background:'var(--border)', borderRadius:4, height:6, overflow:'hidden' }}>
                                            <div style={{ width:Math.min(Math.abs(y.margin),100)+'%', background:y.margin>=0?'var(--green)':'var(--red)', height:'100%', borderRadius:4 }} />
                                          </div>
                                          <span style={{ color:y.margin>=0?'var(--green)':'var(--red)', fontWeight:700, minWidth:42, textAlign:'right' }}>{pct(y.margin)}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Monthly Margin History */}
                      {sorted.length > 1 && (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:10 }}>MONTHLY MARGIN HISTORY</div>
                          <div className="card" style={{ overflow:'hidden' }}>
                            <table className="table">
                              <thead><tr><th>MONTH</th><th>INCOME</th><th>NET PROFIT</th><th>MARGIN</th><th>TREND</th></tr></thead>
                              <tbody>
                                {sorted.map((m,mi) => {
                                  const net2=(parseFloat(m.income)||0)-totalExp(m);
                                  const mg2=parseFloat(m.income)>0?net2/parseFloat(m.income)*100:0;
                                  const pm=sorted[mi+1];
                                  const pmg=pm&&parseFloat(pm.income)>0?((parseFloat(pm.income)||0)-totalExp(pm))/parseFloat(pm.income)*100:null;
                                  const diff2=pmg!==null?mg2-pmg:null;
                                  return (
                                    <tr key={m.month}>
                                      <td style={{ fontWeight:500 }}>{m.month}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(m.income)}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:net2>=0?'var(--green)':'var(--red)', fontWeight:600 }}>{fmt(net2)}</td>
                                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:mg2>=0?'var(--green)':'var(--red)', fontWeight:700 }}>{pct(mg2)}</td>
                                      <td style={{ fontSize:11, color:diff2===null?'var(--text-tertiary)':diff2>=0?'var(--green)':'var(--red)' }}>
                                        {diff2===null?'—':(diff2>=0?'▲ +':'▼ ')+diff2.toFixed(1)+'%'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Financials with year nav */}
                      <div style={{ marginBottom:20 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em' }}>FINANCIALS</div>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:brYrIdx<brAllYrs.length-1?1:0.3 }} onClick={() => { if(brYrIdx<brAllYrs.length-1) setPortalYear(String(brAllYrs[brYrIdx+1])); }}>←</button>
                            <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{brCurYr}</span>
                            <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:brYrIdx>0?1:0.3 }} onClick={() => { if(brYrIdx>0) setPortalYear(String(brAllYrs[brYrIdx-1])); }}>→</button>
                          </div>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                          {[{l:'YEAR INCOME',v:fmt(brYInc),c:'var(--blue)'},{l:'YEAR EXPENSES',v:fmt(brYExp),c:'var(--red)'},{l:'YEAR NET PROFIT',v:fmt(brYInc-brYExp),c:brYInc-brYExp>=0?'var(--green)':'var(--red)'}].map(item=>(
                            <div key={item.l} style={{ background:'var(--bg)', borderRadius:'var(--r-md)', padding:'10px 14px' }}>
                              <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.1em', marginBottom:3 }}>{item.l}</div>
                              <div style={{ color:item.c, fontWeight:700, fontSize:16 }}>{item.v}</div>
                            </div>
                          ))}
                        </div>
                        <div className="card" style={{ overflow:'hidden', overflowX:'auto' }}>
                          <table className="table">
                            <thead><tr><th>MONTH</th><th>INCOME</th><th>RENT</th><th>SALARIES</th><th>ROYALTY</th><th>OTHER</th><th>TOTAL EXP</th><th>NET PROFIT</th></tr></thead>
                            <tbody>
                              {brYrMths.length===0 ? <tr><td colSpan={8} style={{ padding:'22px 14px', color:'var(--text-tertiary)', textAlign:'center' }}>No data yet</td></tr>
                                : brYrMths.map(m => { const exp3=totalExp(m); const net3=(parseFloat(m.income)||0)-exp3; return (
                                  <tr key={m.month}>
                                    <td style={{ color:'var(--text-secondary)' }}>{m.month}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{fmt(m.income)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.rent||0)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.salaries||0)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.royalty||0)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(m.otherExpenses||0)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--red)', fontWeight:500 }}>{fmt(exp3)}</td>
                                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:net3>=0?'var(--green)':'var(--red)', fontWeight:600 }}>{fmt(net3)}</td>
                                  </tr>
                                ); })
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Franchisee P&L per branch */}
                      {owners.length > 0 && plMths.length > 0 && (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em' }}>FRANCHISEE PROFIT & LOSS</div>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:plAllYrs.indexOf(invYear)<plAllYrs.length-1?1:0.3 }} onClick={() => { const i=plAllYrs.indexOf(invYear); if(i<plAllYrs.length-1) setPortalYear(plAllYrs[i+1]); }}>←</button>
                              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{invYear}</span>
                              <button className="btn btn-sm btn-secondary" style={{ padding:'5px 10px', opacity:plAllYrs.indexOf(invYear)>0?1:0.3 }} onClick={() => { const i=plAllYrs.indexOf(invYear); if(i>0) setPortalYear(plAllYrs[i-1]); }}>→</button>
                            </div>
                          </div>
                          <div className="card" style={{ overflow:'hidden' }}>
                            <table className="table">
                              <thead><tr>
                                <th>MONTH</th>
              <th>FRANCHISEE</th><th style={{ textAlign:'right' }}>NET PROFIT</th>
                                <th style={{ textAlign:'right', color:'var(--green)' }}>SHARE</th>
                                <th style={{ textAlign:'right', color:'var(--blue)' }}>SALARY</th>
                                <th style={{ textAlign:'right' }}>TH</th>
                              </tr></thead>
                              <tbody>
                                {plMths.map(m => {
                                  const net4=(parseFloat(m.income)||0)-totalExp(m);
                                  const allSals4=owners.reduce((s,o)=>s+getSal(f,o.email,m.month),0);
                                  const afterSal4=net4-allSals4;
                                  return owners.map((o,oi) => {
                                    const isMe=(o.email||'').toLowerCase()===userEmail;
                                    const oSal4=getSal(f,o.email,m.month);
                                    const share4=afterSal4*getShare(f.owners||[],o.email,m.month)/100;
                                    const th4=oSal4+share4;
                                    return (
                                      <tr key={m.month+'_'+oi} style={{ borderBottom:oi===owners.length-1?'2px solid var(--border)':'1px solid rgba(28,25,96,0.03)', background:isMe?'rgba(15,110,86,0.02)':undefined }}>
                                        {oi===0 && <td rowSpan={owners.length} style={{ color:'var(--text-secondary)', verticalAlign:'top' }}>{m.month}</td>}
                                        <td style={{ color:isMe?'var(--navy)':'var(--text-secondary)', fontWeight:isMe?600:400 }}>
                                          {(o.name||'').split(' ')[0]} <span style={{ color:'var(--text-tertiary)', fontSize:10 }}>({o.sharePercent}%)</span>
                                          {isMe && <span style={{ marginLeft:6, fontSize:9, color:'var(--green)', background:'rgba(15,110,86,0.1)', padding:'2px 6px', borderRadius:10 }}>you</span>}
                                        </td>
                                        {oi===0 && <td rowSpan={owners.length} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:net4>=0?'var(--blue)':'var(--red)', verticalAlign:'top' }}>{fmt(net4)}</td>}
                                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:share4>=0?'var(--green)':'var(--red)' }}>{fmt(share4)}</td>
                                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{oSal4>0?fmt(oSal4):'—'}</td>
                                        <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:isMe?700:400, color:isMe?'var(--green)':'var(--text-secondary)' }}>{fmt(th4)}</td>
                                      </tr>
                                    );
                                  });
                                })}
                                {owners.map((o,oi) => {
                                  const isMe=(o.email||'').toLowerCase()===userEmail;
                                  const tShare5=plMths.reduce((s,m)=>{const net5=(parseFloat(m.income)||0)-totalExp(m);const allS5=owners.reduce((ss,oo)=>ss+getSal(f,oo.email,m.month),0);return s+(net5-allS5)*getShare(f.owners||[],o.email,m.month)/100;},0);
                                  const tSal5=plMths.reduce((s,m)=>s+getSal(f,o.email,m.month),0);
                                  return (
                                    <tr key={'tot'+oi} style={{ borderTop:oi===0?'2px solid var(--border)':'none', background:'rgba(28,25,96,0.06)', fontWeight:700 }}>
                                      {oi===0 && <td rowSpan={owners.length} style={{ fontSize:11, color:'var(--text-tertiary)', verticalAlign:'top' }}>YEAR TOTAL</td>}
                                      <td style={{ color:isMe?'var(--navy)':'var(--text-secondary)', fontSize:11 }}>{(o.name||'').split(' ')[0]}</td>
                                      {oi===0 && <td rowSpan={owners.length} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, verticalAlign:'top' }}>{fmt(plMths.reduce((s,m)=>s+(parseFloat(m.income)||0)-totalExp(m),0))}</td>}
                                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(tShare5)}</td>
                                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{tSal5>0?fmt(tSal5):'—'}</td>
                                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:isMe?'var(--green)':'var(--text-secondary)' }}>{fmt(tShare5+tSal5)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        )}
      </div>
    </div>
  );
}

// ─── Portal Declarations ───────────────────────────────────────────────────────
function PortalDeclarations({ user, data, onReload }) {
  const { declarations, franchises } = data;
  const [showToast, Toast] = useToast();
  const [newModal, setNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ franchiseId:'', month:'', income:'', rent:'', salaries:'', royalty:'', otherExpenses:'', paymentType:'save_only' });
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editRequestModal, setEditRequestModal] = useState(null);
  const [editRequestForm, setEditRequestForm] = useState({ income:'', rent:'', salaries:'', royalty:'', otherExpenses:'' });
  const [editRequestNote, setEditRequestNote] = useState('');
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const userEmail = (user?.email||'').toLowerCase();

  const myDecls = declarations.filter(d => {
    const myFranchiseIds2 = new Set(franchises.filter(f => (f.owners||[]).some(o=>(o.email||'').toLowerCase()===userEmail)).map(f=>f.id));
    return d.franchiseeEmail?.toLowerCase()===userEmail || d.franchiseeId===user?.id || myFranchiseIds2.has(d.franchiseId);
  }).map(d => ({
    ...d,
    branchLocation: franchises.find(f=>f.id===d.franchiseId)?.location || null,
  }));
  const myFranchises = franchises.filter(f =>
    (f.owners||[]).some(o => (o.email||'').toLowerCase()===userEmail)
  );

  const allMonths = [...new Set(myDecls.map(d=>d.month).filter(Boolean))].sort((a,b)=>{
    const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const pm=a.split(' '),qm=b.split(' ');
    return (parseInt(qm[1])*12+MTHS.indexOf(qm[0]))-(parseInt(pm[1])*12+MTHS.indexOf(pm[0]));
  });

  const filteredDecls = myDecls.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q || (d.franchiseName||'').toLowerCase().includes(q) || (d.month||'').toLowerCase().includes(q) || (d.branchLocation||'').toLowerCase().includes(q);
    const matchBranch = filterBranch==='all' || d.franchiseId===filterBranch;
    const matchMonth = filterMonth==='all' || d.month===filterMonth;
    return matchSearch && matchBranch && matchMonth;
  });

  const submitEditRequest = async () => {
    if (!editRequestNote.trim()) { showToast('Please explain what needs to be corrected'); return; }
    await api.requestEdit(editRequestModal.id, {
      note: editRequestNote,
      amendedIncome: parseFloat(editRequestForm.income)||null,
      amendedRent: parseFloat(editRequestForm.rent)||null,
      amendedSalaries: parseFloat(editRequestForm.salaries)||null,
      amendedRoyalty: parseFloat(editRequestForm.royalty)||null,
      amendedOtherExpenses: parseFloat(editRequestForm.otherExpenses)||null,
    });
    showToast('Edit request sent to admin'); setEditRequestModal(null); setEditRequestNote(''); onReload();
  };

  const submit = async () => {
    if (!newForm.franchiseId || !newForm.month) { showToast('Please fill in branch and month'); return; }
    const franchise = franchises.find(f => f.id === newForm.franchiseId);
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const parts = (newForm.month||'').split(' ');
    const fullMonth = MONTHS[SHORT.indexOf(parts[0])] || parts[0];
    const year = parts[1] || '';
    const royalty = parseFloat(newForm.royalty)||0;
    const revenue = parseFloat(newForm.income)||0;

    if (newForm.paymentType === 'submit_and_pay') {
      const jotformUrl = 'https://form.jotform.com/252783975723067'
        + '?name[first]=' + encodeURIComponent((user?.name||'').split(' ')[0])
        + '&name[last]=' + encodeURIComponent((user?.name||'').split(' ').slice(1).join(' '))
        + '&branch2=' + encodeURIComponent(franchise?.location||franchise?.name||'')
        + '&month2=' + encodeURIComponent(fullMonth)
        + '&year2=' + encodeURIComponent(year)
        + '&totalRevenue=' + encodeURIComponent(revenue.toFixed(2))
        + '&royalty=' + encodeURIComponent(royalty.toFixed(2))
        + '&myProducts[price][0]=' + encodeURIComponent(royalty.toFixed(2))
        + '&franchise_id=' + encodeURIComponent(franchise?.id||'');
      const jotWin = window.open(jotformUrl, '_blank');
      if (!jotWin) window.location.href = jotformUrl;
    }

    await api.submitDeclaration({ ...newForm, franchiseName:franchise?.name, franchiseeId:user?.id, franchiseeEmail:user?.email });
    await api.logActivity('Submitted declaration', `${franchise?.location||franchise?.name} — ${newForm.month}`);
    showToast(newForm.paymentType === 'submit_and_pay' ? 'Submitted — completing payment on JotForm.' : 'Declaration submitted');
    setNewModal(false); onReload();
  };

  return (
    <div style={{ marginLeft:'var(--sidebar-width)' }}>
      <Topbar title="My Declarations" actions={
        <button className="btn btn-primary btn-sm" onClick={() => setNewModal(true)}>+ Submit Declaration</button>
      } />
      {Toast}
      <div style={{ padding:24 }}>
        {/* Filters */}
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:200 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by branch or month..." />
          </div>
          <select value={filterBranch} onChange={e=>setFilterBranch(e.target.value)} style={{ minWidth:160 }}>
            <option value="all">All Branches</option>
            {myFranchises.map(f => <option key={f.id} value={f.id}>{f.location||f.name}{f.location&&f.location!==f.name?' — '+f.name:''}</option>)}
          </select>
          <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{ minWidth:130 }}>
            <option value="all">All Months</option>
            {allMonths.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="card" style={{ overflow:'hidden' }}>
          {filteredDecls.length === 0 ? <EmptyState icon="ti-file-text" message="No declarations found" /> : (
            <table className="table" style={{ fontSize:12 }}>
              <thead><tr>
                <th>BRANCH</th><th>MONTH</th>
                <th style={{ textAlign:'right' }}>INCOME</th>
                <th style={{ textAlign:'right' }}>RENT</th>
                <th style={{ textAlign:'right' }}>SALARIES</th>
                <th style={{ textAlign:'right' }}>ROYALTY</th>
                <th style={{ textAlign:'right' }}>OTHER</th>
                <th style={{ textAlign:'right' }}>NET</th>
                <th>PAYMENT</th><th>STATUS</th><th></th>
              </tr></thead>
              <tbody>
                {filteredDecls.sort((a,b) => {
                  const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const pm=a.month?.split(' ')||[], qm=b.month?.split(' ')||[];
                  return (parseInt(qm[1])*12+MTHS.indexOf(qm[0]))-(parseInt(pm[1])*12+MTHS.indexOf(pm[0]));
                }).map(d => {
                  const income = d.amendedIncome!=null?parseFloat(d.amendedIncome):parseFloat(d.income)||0;
                  const royalty = d.amendedRoyalty!=null?parseFloat(d.amendedRoyalty):parseFloat(d.royalty)||0;
                  const rent = d.amendedRent!=null?parseFloat(d.amendedRent):parseFloat(d.rent)||0;
                  const salaries = d.amendedSalaries!=null?parseFloat(d.amendedSalaries):parseFloat(d.salaries)||0;
                  const other = d.amendedOtherExpenses!=null?parseFloat(d.amendedOtherExpenses):parseFloat(d.otherExpenses)||0;
                  const net = income - royalty - rent - salaries - other;
                  const canEdit = d.status === 'pending';
                  const canRequestEdit = d.status === 'approved' && !d.editRequested;
                  const hasAmend = d.amendedIncome!=null;
                  return (
                    <tr key={d.id}>
                      <td style={{ fontWeight:500 }}>
                        <div>{d.branchLocation || d.franchiseName}</div>
                        {d.branchLocation && <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{d.franchiseName}</div>}
                      </td>
                      <td>{d.month}</td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace' }}>
                        {hasAmend ? <div><div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.income)}</div><div style={{ fontWeight:600 }}>{fmt(income)}</div></div> : fmt(income)}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', color:'var(--text-secondary)' }}>
                        {hasAmend && d.amendedRent!=null ? <div><div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.rent)}</div><div style={{ fontWeight:600 }}>{fmt(rent)}</div></div> : fmt(rent)}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', color:'var(--text-secondary)' }}>
                        {hasAmend && d.amendedSalaries!=null ? <div><div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.salaries)}</div><div style={{ fontWeight:600 }}>{fmt(salaries)}</div></div> : fmt(salaries)}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace' }}>
                        {hasAmend && d.amendedRoyalty!=null ? <div><div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.royalty)}</div><div style={{ fontWeight:600 }}>{fmt(royalty)}</div></div> : fmt(royalty)}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', color:'var(--text-secondary)' }}>
                        {hasAmend && d.amendedOtherExpenses!=null ? <div><div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.otherExpenses)}</div><div style={{ fontWeight:600 }}>{fmt(other)}</div></div> : fmt(other)}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontWeight:600, color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</td>
                      <td><Badge type={d.paymentStatus==='confirmed'?'green':d.paymentType==='submit_and_pay'?'amber':'navy'}>
                        {d.paymentType==='submit_and_pay'
                          ? (d.paymentStatus==='confirmed' ? '✓ Paid' : '⏳ Pending Payment')
                          : '💾 Save Only'}
                      </Badge></td>
                      <td>
                        {d.editRequested ? <Badge type="amber">Edit Requested</Badge> :
                         d.editRequestApproved ? <Badge type="blue">✎ Edited</Badge> :
                         <StatusBadge status={d.status} />}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          {canEdit && (
                            <button className="btn btn-sm btn-secondary" onClick={() => { setEditModal(d); setEditForm({income:d.income,rent:d.rent,salaries:d.salaries,royalty:d.royalty,otherExpenses:d.otherExpenses}); }}>Edit</button>
                          )}
                          {canRequestEdit && (
                            <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={() => {
                              setEditRequestModal(d);
                              setEditRequestForm({ income:d.income||'', rent:d.rent||'', salaries:d.salaries||'', royalty:d.royalty||'', otherExpenses:d.otherExpenses||'' });
                              setEditRequestNote('');
                            }}>Request Edit</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* New Declaration Modal */}
      {newModal && (
        <Modal title="Submit Declaration" onClose={() => setNewModal(false)} footer={<>
          <button className="btn btn-secondary" onClick={() => setNewModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Submit</button>
        </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>BRANCH *</label>
              <select value={newForm.franchiseId} onChange={e=>setNewForm(f=>({...f,franchiseId:e.target.value}))}>
                <option value="">Select your branch...</option>
                {myFranchises.map(f=><option key={f.id} value={f.id}>{f.location||f.name}{f.location&&f.location!==f.name?' — '+f.name:''}</option>)}
              </select>
            </div>
            <div><label>MONTH *</label>
              <select value={newForm.month} onChange={e=>setNewForm(f=>({...f,month:e.target.value}))}>
                <option value="">Select month...</option>
                {(() => {
                  const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const now = new Date();
                  const endIdx = now.getFullYear()*12+now.getMonth(); // current month only
                  const activeFranchise = myFranchises.find(f=>f.id===newForm.franchiseId) || (myFranchises.length===1?myFranchises[0]:null);
                  const startMonth = activeFranchise?.royaltyStartMonth || 'Sep 2025';
                  const startParts = startMonth.split(' ');
                  const startIdx = parseInt(startParts[1])*12+MTHS.indexOf(startParts[0]);
                  const activeFranchiseId = activeFranchise?.id || null;
                  const submittedMonths = new Set(
                    activeFranchiseId
                      ? declarations.filter(d => d.franchiseId===activeFranchiseId).map(d=>d.month)
                      : []
                  );
                  const options = [];
                  for (let i=startIdx; i<=endIdx; i++) {
                    const yr=Math.floor(i/12), mo=i%12;
                    const mStr=MTHS[mo]+' '+yr;
                    if (!submittedMonths.has(mStr)) options.push(mStr);
                  }
                  if (options.length === 0) return <option value="">All months submitted ✓</option>;
                  return options.reverse().map(m => <option key={m} value={m}>{m}</option>);
                })()}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[['INCOME','income'],['RENT','rent'],['SALARIES','salaries'],['OTHER EXPENSES','otherExpenses']].map(([l,k]) => (
                <div key={k}><label>{l} (£)</label><input type="number" value={newForm[k]||''} onChange={e=>{
                  const val = e.target.value;
                  if (k === 'income') {
                    const royalty = val ? (parseFloat(val) * 0.125).toFixed(2) : '';
                    setNewForm(f=>({...f, income:val, royalty }));
                  } else {
                    setNewForm(f=>({...f,[k]:val}));
                  }
                }} /></div>
              ))}
              <div><label>ROYALTY (£) <span style={{ fontSize:10, color:'var(--text-tertiary)' }}>12.5% of income</span></label>
                <input type="number" value={newForm.royalty||''} readOnly
                  style={{ background:'var(--bg)', color:'var(--text-secondary)', cursor:'not-allowed' }} />
              </div>
            </div>
            <div><label>PAYMENT TYPE</label>
              <select value={newForm.paymentType} onChange={e=>setNewForm(f=>({...f,paymentType:e.target.value}))}>
                <option value="save_only">Save Only</option>
                <option value="submit_and_pay">Submit & Pay via JotForm</option>
              </select>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Declaration Modal */}
      {editModal && (
        <Modal title={`Edit — ${editModal.franchiseName} ${editModal.month}`} onClose={() => setEditModal(null)} footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            await api.updateDeclaration(editModal.id, editForm);
            showToast('Updated'); setEditModal(null); onReload();
          }}>Save</button>
        </>}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['INCOME','income'],['RENT','rent'],['SALARIES','salaries'],['ROYALTY','royalty'],['OTHER EXPENSES','otherExpenses']].map(([l,k]) => (
              <div key={k}><label>{l} (£)</label><input type="number" value={editForm[k]||''} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))} /></div>
            ))}
          </div>
        </Modal>
      )}

      {/* Request Edit Modal */}
      {editRequestModal && (
        <Modal title="Request Edit" onClose={() => setEditRequestModal(null)} width={500}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setEditRequestModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitEditRequest}>Submit Request</button>
          </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--text-secondary)' }}>
              Enter the corrected figures and explain what needs to be changed. An admin will review and approve your request.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div><label>CORRECTED INCOME (£)</label><input type="number" min="0" step="0.01" value={editRequestForm.income} onChange={e=>setEditRequestForm(f=>({...f,income:e.target.value}))} /></div>
              <div><label>CORRECTED RENT (£)</label><input type="number" min="0" step="0.01" value={editRequestForm.rent} onChange={e=>setEditRequestForm(f=>({...f,rent:e.target.value}))} /></div>
              <div><label>CORRECTED SALARIES (£)</label><input type="number" min="0" step="0.01" value={editRequestForm.salaries} onChange={e=>setEditRequestForm(f=>({...f,salaries:e.target.value}))} /></div>
              <div><label>CORRECTED ROYALTY (£)</label><input type="number" min="0" step="0.01" value={editRequestForm.royalty} onChange={e=>setEditRequestForm(f=>({...f,royalty:e.target.value}))} /></div>
              <div><label>CORRECTED OTHER EXP (£)</label><input type="number" min="0" step="0.01" value={editRequestForm.otherExpenses} onChange={e=>setEditRequestForm(f=>({...f,otherExpenses:e.target.value}))} /></div>
            </div>
            <div><label>REASON FOR EDIT *</label>
              <textarea value={editRequestNote} onChange={e=>setEditRequestNote(e.target.value)}
                placeholder="Explain what was incorrect and why..." rows={3}
                style={{ resize:'none' }} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Portal Royalties ──────────────────────────────────────────────────────────

// ─── Portal Investor P&L Table (view-only for franchisees) ────────────────────
function PortalInvestorPLTable({ franchise, franchiseProfit, getSal, plYear, setPlYear, isOwner }) {
  const [payments, setPayments] = useState({});
  const [selectedMonths, setSelectedMonths] = useState({});
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.getInvestorPayments(franchise.id).then(data => {
      if (data?.payments) {
        const map = {};
        data.payments.forEach(p => { map[p.month] = { paid: p.paid, datePaid: p.date_paid||'', method: p.method||'' }; });
        setPayments(map);
      }
    }).catch(() => {});
  }, [franchise.id]);

  const investors = (franchiseProfit.investors||[]);
  const owners = (franchiseProfit.owners||[]);
  const plFinancials = (franchise.financials||[]).filter(m => (m.month||'').includes(String(plYear))).sort((a,b) => new Date('01 '+b.month)-new Date('01 '+a.month));

  function getInvSal(f, email, month) {
    if (!f || !f.salaries) return 0;
    const sals = (f.salaries||[]).filter(s => {
      const salEmail = (s.userEmail||s.user_email||s.email||'').toLowerCase();
      return salEmail === (email||'').toLowerCase();
    });
    if (!sals.length) return 0;
    const applicable = sals.filter(s => {
      const ef = s.effectiveFrom || s.effective_from || '';
      return new Date('01 ' + ef) <= new Date('01 ' + month);
    }).sort((a,b) => {
      const ea = a.effectiveFrom || a.effective_from || '';
      const eb = b.effectiveFrom || b.effective_from || '';
      return new Date('01 ' + eb) - new Date('01 ' + ea);
    });
    return applicable.length ? parseFloat(applicable[0].amount) : 0;
  }
  function getAllOwnerSals(month) {
    return owners.reduce((s,o) => s + getInvSal(franchise, o.email, month), 0);
  }

  if (!investors.length || !plFinancials.length) return null;

  const paidCount = plFinancials.filter(m => payments[m.month]?.paid).length;

  return (
    <div className="card" style={{ overflow:'hidden', margin:'0 16px 16px' }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:13, fontWeight:600, color:'var(--blue)' }}>Investor Profit & Loss</span>
        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
          <button onClick={()=>setPlYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
          <span style={{ fontSize:12, fontWeight:600, minWidth:36, textAlign:'center' }}>{plYear}</span>
          <button onClick={()=>setPlYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>MONTH</th>
              {isOwner && <th style={{ textAlign:'center' }}>SELECT TO PAY</th>}
              <th style={{ textAlign:'right' }}>NET PROFIT</th>
              <th style={{ textAlign:'right' }}>AFTER SALARIES</th>
              {investors.map((inv,i) => (
                <th key={i} style={{ textAlign:'right', color:'var(--blue)', minWidth:110 }}>
                  {(inv.name||'Investor').split(' ').slice(0,2).join(' ')}
                  <div style={{ fontSize:9, color:'var(--text-tertiary)', fontWeight:400 }}>{inv.sharePercent}%</div>
                </th>
              ))}
              <th style={{ textAlign:'center' }}>PAID</th>
              <th>DATE PAID</th>
              <th>METHOD</th>
            </tr>
          </thead>
          <tbody>
            {plFinancials.map(m => {
              const net = (parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
              const allOwnerSals = getAllOwnerSals(m.month);
              const afterSals = net - allOwnerSals;
              const pmt = payments[m.month] || { paid:false, datePaid:'', method:'' };
              return (
                <tr key={m.month} style={{ background: pmt.paid ? 'rgba(15,110,86,0.03)' : undefined }}>
                  <td style={{ fontSize:12 }}>{m.month}</td>
                  {isOwner && (
                    <td style={{ textAlign:'center' }}>
                      {!payments[m.month]?.paid && (
                        <input type="checkbox"
                          checked={!!selectedMonths[m.month]}
                          onChange={e => setSelectedMonths(p => ({...p, [m.month]: e.target.checked}))}
                          style={{ cursor:'pointer', accentColor:'var(--navy)', width:16, height:16 }}
                        />
                      )}
                    </td>
                  )}
                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(afterSals)}</td>
                  {investors.map((inv,ii) => {
                    const invShare = afterSals * parseFloat(inv.sharePercent||0)/100;
                    return (
                      <td key={ii} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:invShare>=0?'var(--blue)':'var(--red)' }}>
                        {fmt(invShare)}
                      </td>
                    );
                  })}
                  <td style={{ textAlign:'center' }}>
                    <span style={{ padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:600,
                      background: pmt.paid ? 'var(--green-bg)' : 'rgba(163,45,45,0.08)',
                      color: pmt.paid ? 'var(--green)' : 'var(--red)' }}>
                      {pmt.paid ? '✓ Paid' : 'No'}
                    </span>
                  </td>
                  <td><span style={{ fontSize:12, color:'var(--text-secondary)' }}>{pmt.datePaid ? new Date(pmt.datePaid).toLocaleDateString('en-GB') : '—'}</span></td>
                  <td><span style={{ fontSize:12, color:'var(--text-secondary)' }}>{pmt.method||'—'}</span></td>
                </tr>
              );
            })}
            <tr style={{ background:'var(--blue-bg)', fontWeight:700, borderTop:'2px solid var(--border)' }}>
              <td style={{ fontSize:12 }}>YEARLY TOTAL {plYear}</td>
              <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                {fmt(plFinancials.reduce((s,m)=>s+(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0)),0))}
              </td>
              <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                {fmt(plFinancials.reduce((s,m)=>{
                  const net=(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                  return s+net-getAllOwnerSals(m.month);
                },0))}
              </td>
              {investors.map((inv,ii) => {
                const total = plFinancials.reduce((s,m)=>{
                  const net=(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                  return s+(net-getAllOwnerSals(m.month))*parseFloat(inv.sharePercent||0)/100;
                },0);
                return <td key={ii} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:13, fontWeight:700, color:total>=0?'var(--blue)':'var(--red)' }}>{fmt(total)}</td>;
              })}
              <td colSpan={3} style={{ fontSize:11, color:'var(--text-tertiary)' }}>{paidCount} / {plFinancials.length} paid</td>
            </tr>
          </tbody>
        </table>
      </div>
      {isOwner && (
        <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
          <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>
            {Object.values(selectedMonths).filter(Boolean).length > 0
              ? `${Object.values(selectedMonths).filter(Boolean).length} month(s) selected`
              : 'Select unpaid months to pay'}
          </div>
          <button
            className="btn btn-primary btn-sm"
            disabled={paying || Object.values(selectedMonths).filter(Boolean).length === 0}
            onClick={async () => {
              const monthsToPay = plFinancials.filter(m => selectedMonths[m.month] && !payments[m.month]?.paid).map(m => m.month);
              if (!monthsToPay.length) return;
              const total = plFinancials.filter(m => monthsToPay.includes(m.month)).reduce((s,m) => {
                const net=(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                const ownerSals=getAllOwnerSals(m.month);
                return s+investors.reduce((is,inv)=>is+(net-ownerSals)*parseFloat(inv.sharePercent||0)/100,0);
              },0);
              setPaying(true);
              try {
                const res = await api.createInvestorPayment({
                  franchiseId: franchise.id,
                  franchiseName: franchise.name || franchise.location,
                  months: monthsToPay,
                  totalAmount: total.toFixed(2)
                });
                if (res.url) window.open(res.url, '_blank');
              } catch(e) {
                alert('Failed to create payment link. Please try again.');
              }
              setPaying(false);
            }}
          >
            {paying ? 'Creating link...' : 'Pay Investor via Square'}
          </button>
        </div>
      )}
    </div>
  );
}

function PortalRoyalties({ user, data }) {
  const { royalties, franchises } = data;
  const userEmail = (user?.email||'').toLowerCase();
  const myFranchises = franchises.filter(f =>
    (f.owners||[]).some(o=>(o.email||'').toLowerCase()===userEmail) ||
    (f.investors||[]).some(i=>(i.email||'').toLowerCase()===userEmail)
  );
  const myRoyalties = royalties.filter(r => myFranchises.some(f=>f.id===r.franchiseId||f.id===r.franchise_id));
  const [year, setYear] = useState(new Date().getFullYear());

  return (
    <div style={{ marginLeft:'var(--sidebar-width)' }}>
      <Topbar title="Royalty History" />
      <div style={{ padding:24 }}>
        {myFranchises.map(f => {
          const fRoy = myRoyalties.filter(r => r.franchiseId===f.id||r.franchise_id===f.id);
          const yearRoy = fRoy.filter(r=>(r.month||'').includes(String(year))).sort((a,b) => {
            const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const pm=a.month?.split(' ')||[], qm=b.month?.split(' ')||[];
            return (parseInt(qm[1])*12+MTHS.indexOf(qm[0]))-(parseInt(pm[1])*12+MTHS.indexOf(pm[0]));
          });
          const total = yearRoy.reduce((s,r)=>s+(parseFloat(r.royalty)||0),0);
          return (
            <div key={f.id} className="card" style={{ overflow:'hidden', marginBottom:16 }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{f.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{year} total: <span style={{ color:'var(--green)', fontWeight:600 }}>{fmt(total)}</span></div>
                </div>
                <div style={{ display:'flex', gap:4 }}>
                  <button onClick={()=>setYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
                  <span style={{ fontSize:12, minWidth:36, textAlign:'center' }}>{year}</span>
                  <button onClick={()=>setYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
                </div>
              </div>
              {yearRoy.length === 0 ? <EmptyState icon="ti-coin" message={`No royalties in ${year}`} /> : (
                <table className="table">
                  <thead><tr><th>MONTH</th><th>ROYALTY</th><th>PAYMENT DATE</th><th>METHOD</th></tr></thead>
                  <tbody>
                    {yearRoy.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight:500 }}>{r.month}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:600, color:'var(--green)' }}>{fmt(r.royalty)}</td>
                        <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{fmtDate(r.paymentDate)}</td>
                        <td><Badge type="navy">{r.paymentMethod||r.source||'—'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
        {myFranchises.length === 0 && <EmptyState icon="ti-coin" message="No franchise data found" />}
      </div>
    </div>
  );
}

// ─── Portal Profit (Full Feature) ─────────────────────────────────────────────
function PortalProfit({ user, data, onReload }) {
  const { profit, franchises } = data;
  const [showToast, Toast] = useToast();
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ amount:'', method:'Bank Transfer', date:'', notes:'' });
  const [obInfoModal, setObInfoModal] = useState(null);
  const [openingBalanceModal, setOpeningBalanceModal] = useState(null);
  const [obForm, setObForm] = useState({ amount:'', notes:'' });
  const [expandedPL, setExpandedPL] = useState({});
  const [expandedHist, setExpandedHist] = useState({});
  const [collapsedFranchises, setCollapsedFranchises] = useState({});
  const [histYear, setHistYear] = useState(new Date().getFullYear());
  const [histSearch, setHistSearch] = useState('');
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const userEmail = (user?.email||'').toLowerCase();
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';

  const myProfit = profit.filter(f =>
    (f.owners||[]).some(o => (o.email||'').toLowerCase() === userEmail) ||
    (f.investors||[]).some(i => (i.email||'').toLowerCase() === userEmail)
  );

  async function saveWithdrawal() {
    if (!withdrawForm.amount || !withdrawForm.date) { showToast('Please enter amount and date'); return; }
    await api.postWithdrawal({
      franchiseId: withdrawModal.franchiseId,
      userId: withdrawModal.userId,
      userEmail: withdrawModal.email,
      amount: parseFloat(withdrawForm.amount),
      method: withdrawForm.method,
      dateTaken: withdrawForm.date,
      notes: withdrawForm.notes || null,
    });
    await api.logActivity('Profit withdrawal', `${withdrawModal.name} — ${withdrawModal.franchiseName} — £${withdrawForm.amount}`);
    showToast('Withdrawal recorded');
    setWithdrawModal(null);
    setWithdrawForm({ amount:'', method:'Bank Transfer', date:'', notes:'' });
    onReload();
  }

  async function saveOpeningBalance() {
    if (!obForm.amount) { showToast('Please enter an amount'); return; }
    await api.setOpeningBalance({ franchiseId: openingBalanceModal.franchiseId, userId: openingBalanceModal.userId, amount: parseFloat(obForm.amount), notes: obForm.notes || null });
    showToast('Opening balance set');
    setOpeningBalanceModal(null);
    onReload();
  }

  return (
    <div style={{ marginLeft:'var(--sidebar-width)' }}>
      <Topbar title="Profit" subtitle="Your profit balances across all branches" />
      {Toast}
      <div style={{ padding:24 }}>
        {myProfit.length === 0 && <div className="card" style={{ padding:48 }}><EmptyState icon="ti-trending-up" message="No profit data" /></div>}
        {myProfit.map(f => {
          const allPeople = [...(f.owners||[]), ...(f.investors||[])];
          const franchise = franchises.find(fr => fr.id === f.franchiseId);
          const isHOC = f.headOfCentreId && f.headOfCentreId === user?.id;
          const noHOCSet = !f.headOfCentreId;

          const isCollapsed = collapsedFranchises[f.franchiseId] === true;
          return (
            <div key={f.franchiseId} className="card" style={{ marginBottom:16, overflow:'hidden' }}>
              {/* Branch header - clickable to collapse */}
              <div style={{ padding:'12px 16px', background:'rgba(28,25,96,0.03)', borderBottom: isCollapsed ? 'none' : '1px solid var(--border)', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                onClick={() => setCollapsedFranchises(p => ({...p, [f.franchiseId]: !isCollapsed}))}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{f.location || f.franchiseName}</div>
                  {f.location && <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{f.franchiseName}</div>}
                </div>
                <i className={`ti ti-chevron-${isCollapsed?'down':'up'}`} style={{ fontSize:16, color:'var(--text-tertiary)' }} />
              </div>

              {!isCollapsed && (<>

              {/* People grid */}
              <div style={{ padding:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {allPeople.map((person, pi) => {
                  const isMe = (person.email||'').toLowerCase() === userEmail;
                  const isOwner = person.type === 'owner';
                  const canTake = isOwner && (isHOC || noHOCSet);
                  const remaining = (parseFloat(person.totalOwed)||0) - (parseFloat(person.totalTaken)||0);
                  const color = isOwner ? 'var(--navy)' : 'var(--blue)';

                  return (
                    <div key={pi} className="card" style={{ padding:14, borderLeft:`3px solid ${color}`, opacity: isMe ? 1 : 0.85 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div>
                          <div style={{ fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                            {person.name}
                            {isMe && <span style={{ fontSize:10, color:'var(--navy)', background:'var(--blue-bg)', padding:'1px 6px', borderRadius:10, fontWeight:500 }}>you</span>}
                          </div>
                          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2 }}>
                            {isOwner ? 'Franchisee' : 'Investor'} · {person.sharePercent}%
                          </div>
                        </div>
                        {canTake && (
                          <button className="btn btn-sm btn-primary" style={{ fontSize:11 }} onClick={() => {
                            setWithdrawModal({ franchiseId:f.franchiseId, userId:person.userId, name:person.name, franchiseName:f.franchiseName, email:person.email });
                            setWithdrawForm({ amount:'', method:'Bank Transfer', date:new Date().toISOString().split('T')[0], notes:'' });
                          }}>+ Take Profit</button>
                        )}
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns: person.openingBalance > 0 ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap:8 }}>
                        {person.openingBalance > 0 && (
                          <div style={{ cursor:'pointer' }} onClick={() => setObInfoModal({ name:person.name, amount:person.openingBalance, notes:person.openingBalanceNotes, date:person.openingBalanceDate })}>
                            <div style={{ fontSize:9, color:'var(--amber)', letterSpacing:'0.07em', marginBottom:2 }}>OPENING BAL. ℹ</div>
                            <div style={{ fontSize:13, fontWeight:700, color:'var(--amber)' }}>{fmt(person.openingBalance)}</div>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>TOTAL OWED</div>
                          <div style={{ fontSize:13, fontWeight:700, color:remaining>=0?'var(--navy)':'var(--red)' }}>{fmt(person.totalOwed)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>TAKEN</div>
                          <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)' }}>{fmt(person.totalTaken)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>REMAINING</div>
                          <div style={{ fontSize:15, fontWeight:800, color:remaining>=0?'var(--green)':'var(--red)' }}>{fmt(remaining)}</div>
                        </div>
                      </div>

                      {/* Opening balance button for admin */}
                      {isAdmin && (
                        <button className="btn btn-sm btn-secondary" style={{ marginTop:10, fontSize:11, width:'100%' }} onClick={() => {
                          setOpeningBalanceModal({ franchiseId:f.franchiseId, userId:person.userId, name:person.name });
                          setObForm({ amount: person.openingBalance||'', notes: person.openingBalanceNotes||'' });
                        }}>Set Opening Balance</button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* P&L and History buttons */}
              {franchise && franchise.financials && franchise.financials.length > 0 && (
                <div style={{ padding:'0 16px 16px' }}>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setExpandedPL(p => ({...p, [f.franchiseId]: !p[f.franchiseId]}))}>
                      {expandedPL[f.franchiseId] ? 'Hide P&L ▲' : 'View Monthly P&L ▼'}
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setExpandedHist(h => ({...h, [f.franchiseId]: !h[f.franchiseId]}))}>
                      {expandedHist[f.franchiseId] ? 'Hide History ▲' : 'Profit Taken History ▼'}
                    </button>
                  </div>

                  {/* Monthly P&L Table */}
                  {expandedPL[f.franchiseId] && (
                    <div style={{ overflowX:'auto', marginBottom:12 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, marginBottom:10 }}>
                        <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={() => setPlYear(y => y-1)}>‹</button>
                        <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{plYear}</span>
                        <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={() => setPlYear(y => y+1)}>›</button>
                      </div>
                      <table className="table">
                        <thead><tr>
                          <th>MONTH</th>
                          <th style={{ textAlign:'right' }}>NET PROFIT</th>
                          {(franchise.owners||[]).filter(o=>o.isCurrent!==false).map((o,oi) => (
                            <th key={oi} style={{ textAlign:'right', color:'var(--green)' }}>{o.name.split(' ')[0]}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(franchise.financials||[]).filter(m => (m.month||'').includes(String(plYear))).sort((a,b) => new Date('01 '+b.month)-new Date('01 '+a.month)).map(m => {
                            const net = (parseFloat(m.income)||0) - (parseFloat(m.rent)||0) - (parseFloat(m.salaries)||0) - (parseFloat(m.royalty)||0) - (parseFloat(m.otherExpenses)||0);
                            const owners = (franchise.owners||[]).filter(o=>o.isCurrent!==false);
                            const allOwnerSals = owners.reduce((s,o) => {
                              const sal = (franchise.salaries||[]).filter(sl => (sl.userEmail||'').toLowerCase()===(o.email||'').toLowerCase()).sort((a,b)=>new Date('01 '+b.effectiveFrom)-new Date('01 '+a.effectiveFrom));
                              return s+(sal.length?parseFloat(sal[0].amount):0);
                            }, 0);
                            return (
                              <tr key={m.month}>
                                <td style={{ fontSize:12 }}>{m.month}</td>
                                <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                                {owners.map((o,oi) => {
                                  const sal = (franchise.salaries||[]).filter(sl => (sl.userEmail||'').toLowerCase()===(o.email||'').toLowerCase()).sort((a,b)=>new Date('01 '+b.effectiveFrom)-new Date('01 '+a.effectiveFrom));
                                  const oSal = sal.length?parseFloat(sal[0].amount):0;
                                  const th = (net-allOwnerSals)*parseFloat(o.sharePercent||0)/100+oSal;
                                  const isMeCol = (o.email||'').toLowerCase()===userEmail;
                                  return <td key={oi} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:isMeCol?700:400, color:th>=0?'var(--green)':'var(--red)' }}>{fmt(th)}</td>;
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Profit Taken History */}
                  {expandedHist[f.franchiseId] && (
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                        <input placeholder="Search transactions..." value={histSearch} onChange={e=>setHistSearch(e.target.value)} style={{ flex:1, minWidth:160, marginBottom:0 }} />
                        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={() => setHistYear(y => y-1)}>‹</button>
                          <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{histYear}</span>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={() => setHistYear(y => y <= new Date().getFullYear() ? y+1 : y)}>›</button>
                        </div>
                      </div>
                      {(() => {
                        const filtered = (f.withdrawals||[]).filter(w => {
                          const yr = w.dateTaken ? new Date(w.dateTaken).getFullYear() : null;
                          if (yr !== histYear) return false;
                          if (!histSearch) return true;
                          const q = histSearch.toLowerCase();
                          return (w.userName||'').toLowerCase().includes(q) || (w.method||'').toLowerCase().includes(q) || (w.notes||'').toLowerCase().includes(q);
                        });
                        if (!filtered.length) return <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>No transactions for {histYear}</div>;
                        return filtered.map((w,wi) => (
                          <div key={wi} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                            <div>
                              <div style={{ fontWeight:600 }}>{w.userName}</div>
                              <div style={{ color:'var(--text-tertiary)', marginTop:2 }}>{w.method} · {w.dateTaken ? new Date(w.dateTaken).toLocaleDateString('en-GB') : ''}</div>
                              {w.notes && <div style={{ color:'var(--text-tertiary)', fontSize:11 }}>{w.notes}</div>}
                            </div>
                            <span style={{ color:'var(--red)', fontWeight:700 }}>-{fmt(w.amount)}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Investor P&L Table - view only for franchisees, separated by divider */}
              {(f.investors||[]).length > 0 && franchise && (franchise.financials||[]).length > 0 && (
                <>
                  <div style={{ margin:'0 16px', borderTop:'2px solid var(--border)', paddingTop:4 }} />
                  <div style={{ padding:'8px 16px 4px', fontSize:11, fontWeight:700, color:'var(--blue)', letterSpacing:'0.07em' }}>INVESTOR SECTION</div>
                  <PortalInvestorPLTable
                    franchise={franchise}
                    franchiseProfit={f}
                    plYear={plYear}
                    setPlYear={setPlYear}
                    isOwner={(f.owners||[]).some(o => (o.email||'').toLowerCase() === (user?.email||'').toLowerCase())}
                  />
                </>
              )}
            </>)}
            </div>
          );
        })}
      </div>

      {/* Opening Balance Info Modal */}
      {obInfoModal && (
        <Modal title="Rolled Over Balance" onClose={() => setObInfoModal(null)}
          footer={<button className="btn btn-secondary" onClick={() => setObInfoModal(null)}>Close</button>}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{obInfoModal.name}</div>
            <div>
              <label>AMOUNT</label>
              <div style={{ fontSize:22, fontWeight:800, color:'var(--amber)' }}>{fmt(obInfoModal.amount)}</div>
            </div>
            {obInfoModal.date && <div><label>DATE ADDED</label><div style={{ fontSize:13 }}>{new Date(obInfoModal.date).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})}</div></div>}
            {obInfoModal.notes && <div><label>NOTES</label><div style={{ fontSize:13 }}>{obInfoModal.notes}</div></div>}
          </div>
        </Modal>
      )}

      {/* Take Profit Modal */}
      {withdrawModal && (
        <Modal title="Take Profit" onClose={() => setWithdrawModal(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setWithdrawModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveWithdrawal}>Save</button>
          </>}>
          <div style={{ marginBottom:12, fontSize:12, color:'var(--text-secondary)' }}>{withdrawModal.name} · {withdrawModal.franchiseName}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label>AMOUNT (£) *</label><input type="number" min="0" step="0.01" placeholder="0.00" value={withdrawForm.amount} onChange={e=>setWithdrawForm(f=>({...f,amount:e.target.value}))} /></div>
              <div><label>DATE *</label><input type="date" value={withdrawForm.date} onChange={e=>setWithdrawForm(f=>({...f,date:e.target.value}))} /></div>
            </div>
            <div>
              <label>METHOD</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                {['Bank Transfer','Card','Cash','Cheque'].map(m => (
                  <button key={m} type="button" onClick={() => setWithdrawForm(f=>({...f,method:m}))} className="btn btn-sm" style={{
                    background: withdrawForm.method===m?'var(--navy)':'#fff',
                    color: withdrawForm.method===m?'#fff':'var(--text-secondary)',
                    border:'1px solid var(--border)',
                  }}>{m}</button>
                ))}
              </div>
            </div>
            <div><label>NOTES (optional)</label><input placeholder="Any notes..." value={withdrawForm.notes} onChange={e=>setWithdrawForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>
        </Modal>
      )}

      {/* Opening Balance Modal */}
      {openingBalanceModal && (
        <Modal title={`Opening Balance — ${openingBalanceModal.name}`} onClose={() => setOpeningBalanceModal(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setOpeningBalanceModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveOpeningBalance}>Save</button>
          </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>AMOUNT (£) *</label><input type="number" value={obForm.amount} onChange={e=>setObForm(f=>({...f,amount:e.target.value}))} placeholder="0.00" /></div>
            <div><label>NOTES</label><input value={obForm.notes} onChange={e=>setObForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes..." /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
